/* eslint-disable max-lines */
import {
  ApiGuardianMetrics,
  CustomWafRule,
  GetWafRuleMetricsResponse,
  RatelimitMetrics,
  ShieldZoneMetrics,
  ShieldZoneResponse,
  ShieldZoneUploadScanningMetrics,
} from '@bn/app-shield';
import { StringNumberMap } from '@bn/shared/entities/index';
import { DateTimeUtils, DAYJS_FORMAT } from '@bn/shared/utils';
import { WAFEngineConfig } from '../../models';
import { ShieldOverviewMetricsData } from '../../openapi/model/shield-overview-metrics-data';
import { ShieldZoneBotDetectionMetrics } from '../../openapi/model/shield-zone-bot-detection-metrics';

export function enrichShieldEntity(shield: ShieldZoneResponse, defaultEngineConfig: WAFEngineConfig[]): ShieldZoneResponse {
  const defaultKeys = ['allowed_http_versions', 'allowed_methods', 'allowed_request_content_type'];
  const defaultKeysToBeAdded = [...defaultKeys];
  const wafEngineConfig = shield.wafEngineConfig ? [...shield.wafEngineConfig] : [];

  shield.wafEngineConfig?.forEach((engineItem) => {
    if (engineItem.name) defaultKeysToBeAdded.splice(defaultKeysToBeAdded.indexOf(engineItem.name), 1);
  });

  // Note: in case there is no items in array with following keys add them and fill with all values
  defaultEngineConfig
    .filter((defaultItem) => defaultKeysToBeAdded.indexOf(defaultItem.name) > -1)
    .map((defaultItem) => {
      wafEngineConfig?.push(defaultItem);
    });

  return {
    ...shield,
    wafEngineConfig,
  };
}

/**
 * Prepends custom WAF rule IDs with 'WAF-' prefix if they match the custom rules array.
 *
 * @param rawRuleIds - Array of rule IDs as strings
 * @param customRules - Array of CustomWafRule objects to check against
 * @returns Array of rule IDs with 'WAF-' prefix added for matching custom rules
 */
export function prependCustomWafRulesId(rawRuleIds: string[], customRules: CustomWafRule[]): string[] {
  return rawRuleIds.map((ruleId) => (customRules.some((rule) => String(rule.id) === ruleId) ? `WAF-${ruleId}` : ruleId));
}

/**
 * Removes the 'WAF-' prefix from WAF rule IDs.
 *
 * @param ruleIds - Array of rule IDs that may contain 'WAF-' prefix
 * @returns Array of rule IDs with 'WAF-' prefix removed
 */
export function stripCustomWafRulePrefix(ruleIds: string[]): string[] {
  return ruleIds.map((ruleId) => ruleId.replace('WAF-', ''));
}

/**
 * Sorts custom WAF rules by an explicit order of rule IDs (ShieldZone.wafCustomRuleOrder).
 * Rules whose id is in `order` come first, in that order; rules not present (e.g. newly created)
 * keep their relative order at the end. A null/empty order returns the input unchanged.
 *
 * @param rules - Custom rules to sort (anything carrying a numeric `id`)
 * @param order - Array of custom rule IDs in the desired display order, or null for default order
 */
export function sortCustomRulesByOrder<T extends { id?: number }>(rules: T[], order?: number[] | null): T[] {
  if (!order?.length) return rules;
  const pos = new Map(order.map((id, index) => [id, index]));
  return [...rules].sort((a, b) => (pos.get(Number(a.id)) ?? Number.MAX_SAFE_INTEGER) - (pos.get(Number(b.id)) ?? Number.MAX_SAFE_INTEGER));
}

export function generateEmptyChartDays(days = 28, seriesCount = 4): (string | number)[][] {
  const today = DateTimeUtils.toUtcTime(new Date());

  return Array.from({ length: days }, (_, i) => [
    today
      .subtract(days - 1 - i, 'day')
      .startOf('day')
      .format(),
    ...Array(seriesCount).fill(0),
  ]);
}

/** Always return dataset even if all default zeros */
export const mapShieldDdosChartData: (metrics?: ShieldZoneMetrics) => ShieldDdosChartData = (metrics) => {
  const total = {
    loggedRequests: metrics?.dDoS?.loggedRequests ?? 0,
    blockedRequests: metrics?.dDoS?.blockedRequests ?? 0,
    challengedRequests: metrics?.dDoS?.challengedRequests ?? 0,
    verifiedRequests: metrics?.dDoS?.verifiedRequests ?? 0,
  };

  const daysData = Object.entries(metrics?.dDoSOverviewPastTwentyEightDays ?? {});
  const chartDataRows =
    daysData.length > 0
      ? daysData.map(([shieldDate, { loggedRequests = 0, blockedRequests = 0, challengedRequests = 0, verifiedRequests = 0 }]) => {
          const isoDate = DateTimeUtils.toUtcTime(shieldDate, DAYJS_FORMAT.ShieldMetricsApi).format();

          return [isoDate, challengedRequests, blockedRequests, loggedRequests, verifiedRequests];
        })
      : generateEmptyChartDays(28, 4);

  return {
    chartSeries: [['time', 'Challenged', 'Blocked', 'Logged', 'Verified'], ...chartDataRows],
    total,
    hasData:
      metrics?.overviewPastTwentyEightDays !== undefined &&
      total.loggedRequests + total.blockedRequests + total.challengedRequests + total.verifiedRequests > 0,
  };
};

export const MapShieldOverviewChartsData: (metricsData?: ShieldOverviewMetricsData) => ShieldOverviewChartsMappedData = (metricsData) => {
  const wafMetrics = metricsData?.waf?.metrics || {};
  const ddosMetrics = metricsData?.ddos?.metrics || {};
  const rateLimitMetrics = metricsData?.rateLimit?.metrics || {};
  const botDetectionMetrics = metricsData?.botDetection?.metrics || {};
  const accessListsMetrics = metricsData?.accessLists?.metrics || {};
  const uploadScanningMetrics = metricsData?.uploadScanning?.metrics || {};
  const apiGuardianMetrics = metricsData?.apiGuardian?.metrics || {};

  const total = {
    WAFAttacks: (metricsData?.waf?.totals?.blocked ?? 0) + (metricsData?.waf?.totals?.challenged ?? 0) + (metricsData?.waf?.totals?.logged ?? 0),
    ddosAttacks: (metricsData?.ddos?.totals?.blocked ?? 0) + (metricsData?.ddos?.totals?.challenged ?? 0) + (metricsData?.ddos?.totals?.verified ?? 0),
    RateLimitAttacks:
      (metricsData?.rateLimit?.totals?.blocked ?? 0) + (metricsData?.rateLimit?.totals?.challenged ?? 0) + (metricsData?.rateLimit?.totals?.logged ?? 0),
    botDetectionChallenged: (metricsData?.botDetection?.totals?.challenged ?? 0) + (metricsData?.botDetection?.totals?.logged ?? 0),
    accessListActions: (metricsData?.accessLists?.totals?.blocked ?? 0) + (metricsData?.accessLists?.totals?.challenged ?? 0),
    uploadScanningBlocks: (metricsData?.uploadScanning?.totals?.blocked ?? 0) + (metricsData?.uploadScanning?.totals?.logged ?? 0),
    apiGuardianActions: (metricsData?.apiGuardian?.totals?.blocked ?? 0) + (metricsData?.apiGuardian?.totals?.logged ?? 0),
  };

  const wafMiniChartSeries: StringNumberMap = {};
  const ddosMiniChartSeries: StringNumberMap = {};
  const rateLimitingMiniChartSeries: StringNumberMap = {};
  const botDetectionMiniChartSeries: StringNumberMap = {};
  const accessListMiniChartSeries: StringNumberMap = {};
  const uploadScanningMiniChartSeries: StringNumberMap = {};
  const apiGuardianMiniChartSeries: StringNumberMap = {};
  const breakdownData: ShieldOverviewBreakdownData = {};

  const allTimestamps = new Set<string>();
  [
    wafMetrics.blocked,
    wafMetrics.challenged,
    wafMetrics.logged,
    ddosMetrics.blocked,
    ddosMetrics.challenged,
    ddosMetrics.verified,
    ddosMetrics.logged,
    rateLimitMetrics.blocked,
    rateLimitMetrics.challenged,
    rateLimitMetrics.logged,
    botDetectionMetrics.challenged,
    botDetectionMetrics.logged,
    accessListsMetrics.blocked,
    accessListsMetrics.challenged,
    uploadScanningMetrics.blocked,
    uploadScanningMetrics.logged,
    apiGuardianMetrics.blocked,
    apiGuardianMetrics.logged,
  ].forEach((metricMap) => {
    if (metricMap) {
      Object.keys(metricMap).forEach((timestamp) => allTimestamps.add(timestamp));
    }
  });

  const sortedTimestamps = Array.from(allTimestamps).sort();

  const chartSeries: (string | number)[][] = [
    ['time', 'WAF', 'DDoS mitigation', 'Rate limiting', 'Bot detection', 'Access lists', 'Upload scanning', 'API Guardian'],
    ...sortedTimestamps.map((timestamp) => {
      const wafValue = (wafMetrics.blocked?.[timestamp] ?? 0) + (wafMetrics.challenged?.[timestamp] ?? 0) + (wafMetrics.logged?.[timestamp] ?? 0);
      const ddosValue =
        (ddosMetrics.blocked?.[timestamp] ?? 0) +
        (ddosMetrics.challenged?.[timestamp] ?? 0) +
        (ddosMetrics.logged?.[timestamp] ?? 0) +
        (ddosMetrics.verified?.[timestamp] ?? 0);
      const rateLimitValue =
        (rateLimitMetrics.blocked?.[timestamp] ?? 0) + (rateLimitMetrics.challenged?.[timestamp] ?? 0) + (rateLimitMetrics.logged?.[timestamp] ?? 0);
      const botDetectionValue = (botDetectionMetrics.challenged?.[timestamp] ?? 0) + (botDetectionMetrics.logged?.[timestamp] ?? 0);
      const accessListValue = (accessListsMetrics.blocked?.[timestamp] ?? 0) + (accessListsMetrics.challenged?.[timestamp] ?? 0);
      const uploadScanningValue = (uploadScanningMetrics.blocked?.[timestamp] ?? 0) + (uploadScanningMetrics.logged?.[timestamp] ?? 0);
      const apiGuardianValue = (apiGuardianMetrics.blocked?.[timestamp] ?? 0) + (apiGuardianMetrics.logged?.[timestamp] ?? 0);

      wafMiniChartSeries[timestamp] = wafValue;
      ddosMiniChartSeries[timestamp] = ddosValue;
      rateLimitingMiniChartSeries[timestamp] = rateLimitValue;
      botDetectionMiniChartSeries[timestamp] = botDetectionValue;
      accessListMiniChartSeries[timestamp] = accessListValue;
      uploadScanningMiniChartSeries[timestamp] = uploadScanningValue;
      apiGuardianMiniChartSeries[timestamp] = apiGuardianValue;

      breakdownData[timestamp] = {
        waf: {
          blocked: wafMetrics.blocked?.[timestamp] ?? 0,
          challenged: wafMetrics.challenged?.[timestamp] ?? 0,
          logged: wafMetrics.logged?.[timestamp] ?? 0,
        },
        ddos: {
          blocked: ddosMetrics.blocked?.[timestamp] ?? 0,
          challenged: ddosMetrics.challenged?.[timestamp] ?? 0,
          logged: ddosMetrics.logged?.[timestamp] ?? 0,
          verified: ddosMetrics.verified?.[timestamp] ?? 0,
        },
        rateLimit: {
          blocked: rateLimitMetrics.blocked?.[timestamp] ?? 0,
          challenged: rateLimitMetrics.challenged?.[timestamp] ?? 0,
          logged: rateLimitMetrics.logged?.[timestamp] ?? 0,
        },
        botDetection: {
          challenged: botDetectionMetrics.challenged?.[timestamp] ?? 0,
          logged: botDetectionMetrics.logged?.[timestamp] ?? 0,
        },
        accessLists: {
          blocked: accessListsMetrics.blocked?.[timestamp] ?? 0,
          challenged: accessListsMetrics.challenged?.[timestamp] ?? 0,
        },
        uploadScanning: {
          blocked: uploadScanningMetrics.blocked?.[timestamp] ?? 0,
          logged: uploadScanningMetrics.logged?.[timestamp] ?? 0,
        },
        apiGuardian: {
          blocked: apiGuardianMetrics.blocked?.[timestamp] ?? 0,
          logged: apiGuardianMetrics.logged?.[timestamp] ?? 0,
        },
      };

      return [timestamp, wafValue, ddosValue, rateLimitValue, botDetectionValue, accessListValue, uploadScanningValue, apiGuardianValue];
    }),
  ];

  return {
    wafMiniChartSeries,
    ddosMiniChartSeries,
    rateLimitingMiniChartSeries,
    botDetectionMiniChartSeries,
    accessListMiniChartSeries,
    uploadScanningMiniChartSeries,
    apiGuardianMiniChartSeries,
    chartSeries,
    breakdownData,
    total,
    hasData:
      sortedTimestamps.length > 0 &&
      total.ddosAttacks +
        total.WAFAttacks +
        total.RateLimitAttacks +
        total.botDetectionChallenged +
        total.accessListActions +
        total.uploadScanningBlocks +
        total.apiGuardianActions >
        0,
  };
};

/** Always return dataset even if all default zeros */
export const MapShieldRateLimitChartData: (metrics?: ShieldZoneMetrics) => ShieldRateLimitsChartData = (metrics) => {
  const total = {
    blockedBreaches: metrics?.ratelimit?.blockedBreaches ?? 0,
    challengedBreaches: metrics?.ratelimit?.challengedBreaches ?? 0,
    loggedBreaches: metrics?.ratelimit?.loggedBreaches ?? 0,
    totalBreaches: metrics?.ratelimit?.totalBreaches ?? 0,
  };
  return {
    chartSeries: [
      ['time', 'HTTP requests', 'Logged requests', 'Blocked requests', 'Mitigated requests'],
      ...Object.entries(metrics?.ratelimitOverviewPastTwentyEightDays ?? {}).map(
        ([shieldDate, { blockedBreaches = 0, challengedBreaches = 0, loggedBreaches = 0, totalBreaches = 0 }]) => {
          const isoDate = DateTimeUtils.toUtcTime(shieldDate, DAYJS_FORMAT.ShieldMetricsApi).format();

          return [isoDate, totalBreaches, loggedBreaches, blockedBreaches, challengedBreaches];
        },
      ),
    ],
    total,
    hasData:
      metrics?.overviewPastTwentyEightDays !== undefined && total.blockedBreaches + total.challengedBreaches + total.loggedBreaches + total.totalBreaches > 0,
  };
};

/** Always return dataset even if all default zeros */
export const MapShieldPerRateLimitChartData: (metrics?: RatelimitMetrics[]) => ShieldPerRateLimitsChartData = (metrics = []) => {
  const result = <ShieldPerRateLimitsChartData>{};
  // Rate Limit has list of RatelimitMetrics, make one big object of all of them, key is ID
  metrics.forEach((rule) => {
    // CustomWafRule
    const total = {
      blockedBreaches: rule?.overview?.blockedBreaches ?? 0,
      challengedBreaches: rule?.overview?.challengedBreaches ?? 0,
      loggedBreaches: rule?.overview?.loggedBreaches ?? 0,
      totalBreaches: rule?.overview?.totalBreaches ?? 0,
    };

    Object.assign(result, {
      // TODO: remove IF IN when swagger is fixed
      ['ratelimitId' in rule ? (rule.ratelimitId as string) : 'missingKey']: {
        chartSeries: [
          ['time', 'HTTP requests', 'Logged requests', 'Blocked requests', 'Mitigated requests'],
          ...Object.entries(rule?.ratelimitOverviewPastTwentyEightDays ?? {}).map(
            ([shieldDate, { blockedBreaches = 0, challengedBreaches = 0, loggedBreaches = 0, totalBreaches = 0 }]) => {
              const isoDate = DateTimeUtils.toUtcTime(shieldDate, DAYJS_FORMAT.ShieldMetricsApi).format();

              return [isoDate, totalBreaches, loggedBreaches, blockedBreaches, challengedBreaches];
            },
          ),
        ],
        total,
        hasData:
          rule?.ratelimitOverviewPastTwentyEightDays !== undefined &&
          total.blockedBreaches + total.challengedBreaches + total.loggedBreaches + total.totalBreaches > 0,
      },
    });
  });

  return result;
};

/** Always return dataset even if all default zeros */
export const MapShieldWafTriggeredRulesChartData: (metrics?: ShieldZoneMetrics) => ShieldWafTriggeredRulesChartData = (metrics) => {
  const total = {
    WAFAttacks: metrics?.overview?.wafTriggeredRules ?? 0,
  };

  return {
    chartSeries: [
      ['time', 'Total WAF triggers'],
      ...Object.entries(metrics?.overviewPastTwentyEightDays ?? {}).map(([shieldDate, { wafTriggeredRules = 0 }]) => {
        const isoDate = DateTimeUtils.toUtcTime(shieldDate, DAYJS_FORMAT.ShieldMetricsApi).format();

        return [isoDate, wafTriggeredRules];
      }),
    ],
    total,
    hasData: metrics?.overviewPastTwentyEightDays !== undefined && total.WAFAttacks > 0,
  };
};

/** Always return dataset even if all default zeros */
export const MapShieldBotDetectionChartData: (metrics?: ShieldZoneBotDetectionMetrics) => ShieldBotDetectionChartData = (metrics) => {
  const total = {
    logged: metrics?.totalLoggedRequests ?? 0,
    challengesServed: metrics?.totalChallengedRequests ?? 0,
  };

  return {
    chartSeries: [
      ['time', 'Logged', 'Challenges served'],
      ...Object.entries(metrics?.overviewPastTwentyEightDays ?? {}).map(([shieldDate, { loggedRequests = 0, challengedRequests = 0 }]) => {
        const isoDate = DateTimeUtils.toUtcTime(shieldDate, DAYJS_FORMAT.ShieldMetricsApi).format();

        return [isoDate, loggedRequests, challengedRequests];
      }),
    ],
    total,
    hasData: metrics?.overviewPastTwentyEightDays !== undefined && ((total.logged ?? 0) > 0 || (total.challengesServed ?? 0) > 0),
  };
};

/** Always return dataset even if all default zeros */
export const MapShieldUploadScanningChartData: (metrics?: ShieldZoneUploadScanningMetrics) => ShieldUploadScanningChartData = (metrics) => {
  const total = {
    totalLoggedRequests: metrics?.totalLoggedRequests ?? 0,
    totalBlockedRequests: metrics?.totalBlockedRequests ?? 0,
    totalFilesScanned: metrics?.totalFilesScanned ?? 0,
  };

  return {
    chartSeries: [
      ['time', 'Files scanned', 'Blocked requests', 'Logged requests'],
      ...Object.entries(metrics?.overviewPastTwentyEightDays ?? {}).map(([shieldDate, { loggedRequests = 0, blockedRequests = 0, filesScanned = 0 }]) => {
        const isoDate = DateTimeUtils.toUtcTime(shieldDate, DAYJS_FORMAT.ShieldMetricsApi).format();

        return [isoDate, filesScanned, blockedRequests, loggedRequests];
      }),
    ],
    total,
    hasData:
      metrics?.overviewPastTwentyEightDays !== undefined &&
      ((total.totalLoggedRequests ?? 0) > 0 || (total.totalBlockedRequests ?? 0) > 0 || (total.totalFilesScanned ?? 0) > 0),
  };
};

/** Always return dataset even if all default zeros */
export const MapShieldWafTriggeredRuleChartData: (metrics?: GetWafRuleMetricsResponse['data']) => ShieldWafTriggeredRuleChartData = (metrics) => {
  return {
    chartSeries: [
      ['time', 'Total triggers', 'Challenged', 'Logged', 'Blocked'],
      ...Object.entries(metrics?.overviewPastTwentyEightDays ?? {}).map(
        ([shieldDate, { totalTriggers = 0, challengedRequests = 0, loggedRequests = 0, blockedRequests = 0 }]) => {
          const isoDate = DateTimeUtils.toUtcTime(shieldDate, DAYJS_FORMAT.ShieldMetricsApi).format();

          return [isoDate, totalTriggers, challengedRequests, loggedRequests, blockedRequests];
        },
      ),
    ],
    total: {
      totalTriggers: metrics?.totalTriggers ?? 0,
      blocked: metrics?.blockedRequests ?? 0,
      logged: metrics?.loggedRequests ?? 0,
      challenged: metrics?.challengedRequests ?? 0,
    },
    hasData:
      metrics?.overviewPastTwentyEightDays !== undefined &&
      (metrics?.totalTriggers ?? 0) + (metrics?.blockedRequests ?? 0) + (metrics?.loggedRequests ?? 0) + (metrics?.challengedRequests ?? 0) > 0,
  };
};

export type ShieldOverviewBreakdownEntry = {
  waf: { blocked: number; challenged: number; logged: number };
  ddos: { blocked: number; challenged: number; logged: number; verified: number };
  rateLimit: { blocked: number; challenged: number; logged: number };
  botDetection: { challenged: number; logged: number };
  accessLists: { blocked: number; challenged: number };
  uploadScanning: { blocked: number; logged: number };
  apiGuardian: { blocked: number; logged: number };
};

export type ShieldOverviewBreakdownData = {
  [timestamp: string]: ShieldOverviewBreakdownEntry;
};

export type ShieldOverviewChartsMappedData = ShieldOverviewChartData & {
  wafMiniChartSeries: StringNumberMap;
  ddosMiniChartSeries: StringNumberMap;
  rateLimitingMiniChartSeries: StringNumberMap;
  botDetectionMiniChartSeries: StringNumberMap;
  accessListMiniChartSeries: StringNumberMap;
  apiGuardianMiniChartSeries: StringNumberMap;
  uploadScanningMiniChartSeries: StringNumberMap;
};

export type ShieldOverviewChartData = {
  chartSeries: (string | number)[][];
  breakdownData: ShieldOverviewBreakdownData;
  total: {
    ddosAttacks: number;
    WAFAttacks: number;
    RateLimitAttacks: number;
    botDetectionChallenged: number;
    accessListActions: number;
    uploadScanningBlocks: number;
    apiGuardianActions: number;
  };
  hasData: boolean;
};

export type ShieldDdosChartData = {
  chartSeries: (string | number)[][];
  total: {
    challengedRequests: number;
    blockedRequests: number;
    loggedRequests: number;
    verifiedRequests: number;
  };
  hasData: boolean;
};

export type ShieldRateLimitsChartData = {
  chartSeries: (string | number)[][];
  total: {
    blockedBreaches: number;
    challengedBreaches: number;
    loggedBreaches: number;
    totalBreaches: number;
  };
  hasData: boolean;
};

export type ShieldPerRateLimitsChartData = {
  [key: string]: {
    chartSeries: (string | number)[][];
    total: {
      blockedBreaches: number;
      challengedBreaches: number;
      loggedBreaches: number;
      totalBreaches: number;
    };
    hasData: boolean;
  };
};

export type ShieldWafTriggeredRulesChartData = {
  chartSeries: (string | number)[][];
  total: {
    WAFAttacks: number;
  };
  hasData: boolean;
};

export type ShieldWafTriggeredRuleChartData = {
  chartSeries: (string | number)[][];
  total: {
    totalTriggers: number;
    blocked: number;
    logged: number;
    challenged: number;
  };
  hasData: boolean;
};

export type ShieldBotDetectionChartData = {
  chartSeries: (string | number)[][];
  total: {
    // challengesFailed: number;
    challengesServed: number;
    logged: number;
  };
  hasData: boolean;
};

export type ShieldUploadScanningChartData = {
  chartSeries: (string | number)[][];
  total: {
    totalLoggedRequests: number;
    totalBlockedRequests: number;
    totalFilesScanned: number;
  };
  hasData: boolean;
};

export type ShieldApiGuardianChartData = {
  chartSeries: (string | number)[][];
  total: {
    totalRequestsInspected: number;
    totalFailedRequestValidation: number;
    totalFailedAuthenticationEnforcement: number;
  };
  hasData: boolean;
};

export const MapShieldApiGuardianChartData: (metrics?: ApiGuardianMetrics) => ShieldApiGuardianChartData = (metrics) => {
  const total = {
    totalRequestsInspected: metrics?.totalRequestsInspected ?? 0,
    totalFailedRequestValidation: metrics?.totalFailedRequestValidation ?? 0,
    totalFailedAuthenticationEnforcement: metrics?.totalFailedAuthenticationEnforcement ?? 0,
  };

  return {
    chartSeries: [
      ['time', 'Total Requests Inspected', 'Total Failed Request Validation', 'Total Failed Authentication Enforcement'],
      ...Object.entries(metrics?.overviewPastTwentyEightDays ?? {}).map(
        ([shieldDate, { requestsInspected = 0, failedRequestValidation = 0, failedAuthenticationEnforcement = 0 }]) => {
          const isoDate = DateTimeUtils.toUtcTime(shieldDate, DAYJS_FORMAT.ShieldMetricsApi).format();

          return [isoDate, requestsInspected, failedRequestValidation, failedAuthenticationEnforcement];
        },
      ),
    ],
    total,
    hasData:
      metrics?.overviewPastTwentyEightDays !== undefined &&
      (total.totalRequestsInspected > 0 || total.totalFailedRequestValidation > 0 || total.totalFailedAuthenticationEnforcement > 0),
  };
};
